import React, { useState } from 'react';
import { Box, Typography, Link, Paper, Chip, Snackbar, Alert, Tooltip } from '@mui/material';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import { TicketEasyService } from '../../../../utils/ServiceLocator';
import { getToken } from '../../../../utils/AdalConfig';
import { Attachment } from '../../../../services/ticketEasyService/model/ticketEasy/ticketAttachments/Attachment';

interface MessageAttachmentsProps {
    attachments?: Attachment[];
    ticketId: string;
}

export function MessageAttachments({ attachments = [], ticketId }: MessageAttachmentsProps) {
    const activeAttachments = attachments.filter(a => !a.isDeleted);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    
    if (!attachments || activeAttachments.length === 0) {
        return null;
    }
    const handleDownload = async (documentId: string, fileName: string) => {
        try {
            // Get authorization token
            const token = await getToken();
            
            // Create download URL
            const downloadUrl = `${TicketEasyService}DocumentDownload/${documentId}`;
            
            // Make a fetch request with proper authorization
            const response = await fetch(downloadUrl, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            
            if (!response.ok) {
                throw new Error(`Error downloading file: ${response.status} ${response.statusText}`);
            }
            
            // Get the file content as blob
            const blob = await response.blob();
            
            // Check if the file is a PDF
            const isPdf = fileName.toLowerCase().endsWith('.pdf');
            
            if (isPdf) {
                // For PDFs, open in a new tab
                const file = new Blob([blob], { type: 'application/pdf' });
                const url = window.URL.createObjectURL(file);
                window.open(url);
            } else {
                // For other file types, trigger a download
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', fileName);
                document.body.appendChild(link);
                link.click();
                
                // Clean up
                setTimeout(() => {
                    window.URL.revokeObjectURL(url);
                    document.body.removeChild(link);
                }, 100); // Small timeout to ensure download starts before cleanup
            }
        } catch (error) {
            console.error('Error downloading attachment:', error);
            setErrorMessage('Failed to download the file. Please try again.');
        }
    };
    
    return (
        <Box sx={{ mb: 2 }}>
            <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', display: 'flex', alignItems: 'center' }}>
                Attachments ({activeAttachments.length})
                <AttachFileIcon fontSize="small" sx={{ ml: 0.5, fontSize: '1rem' }} />
            </Typography>
            <Paper variant="outlined" sx={{ p: 1.5, bgcolor: '#f9f9f9', mt: 0.5, display: 'flex', flexWrap: 'wrap', gap: 1 }}>                {activeAttachments.map((attachment, index) => (
                    <Tooltip title={attachment.fileName} key={`tooltip-${attachment.id}-${index}`}>
                        <Chip
                            key={`${attachment.id}-${index}`}
                            label={attachment.fileName}
                            icon={attachment.fileName.toLowerCase().endsWith('.pdf') ? 
                                <PictureAsPdfIcon fontSize="small" /> : 
                                <AttachFileIcon fontSize="small" />
                            }
                            variant="outlined"
                            color="primary"
                            clickable
                            onClick={() => handleDownload(attachment.id, attachment.fileName)}
                            size="small"
                            sx={{ 
                                '&:hover': { 
                                    backgroundColor: '#e3f2fd',
                                    boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
                                },
                                transition: 'all 0.2s ease',
                                fontWeight: 400,
                                padding: '4px 0',
                                maxWidth: '250px',
                                '& .MuiChip-label': {
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap'
                                }
                            }}
                        />
                    </Tooltip>
                ))}
            </Paper>
            
            <Snackbar 
                open={!!errorMessage} 
                autoHideDuration={6000} 
                onClose={() => setErrorMessage(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert onClose={() => setErrorMessage(null)} severity="error" sx={{ width: '100%' }}>
                    {errorMessage}
                </Alert>
            </Snackbar>
        </Box>
    );
}
